import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, KeyRound, ShieldCheck, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STEP = {
  EMAIL: 'email',
  ANSWERS: 'answers',
  NEW_PASSWORD: 'new_password',
  DONE: 'done',
};

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(STEP.EMAIL);
  const [email, setEmail] = useState('');
  const [questions, setQuestions] = useState([]); // [{question_id, prompt}]
  const [answers, setAnswers] = useState({});
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const readBody = async (r) => {
    try {
      const t = await r.text();
      if (!t) return {};
      try {
        return JSON.parse(t);
      } catch {
        return { detail: t };
      }
    } catch {
      return {};
    }
  };

  const lookupQuestions = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!email.trim()) {
      setError('Enter your email first.');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/auth/security-questions-for-email?email=${encodeURIComponent(
          email.trim(),
        )}`,
      );
      const data = await readBody(r);
      if (!data.configured) {
        setError(
          'No security questions are set up for this account. Please ask an admin to reset your password.',
        );
        return;
      }
      setQuestions(data.questions || []);
      setAnswers({});
      setStep(STEP.ANSWERS);
    } catch (err) {
      setError(err.message || 'Failed to look up questions.');
    } finally {
      setLoading(false);
    }
  };

  const submitAnswers = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (questions.some((q) => !(answers[q.question_id] || '').trim())) {
      setError('Answer every question.');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/auth/verify-security-answers`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email.trim(),
            answers: questions.map((q) => ({
              question_id: q.question_id,
              answer: answers[q.question_id],
            })),
          }),
        },
      );
      const data = await readBody(r);
      if (!r.ok || !data.reset_token) {
        throw new Error(
          (typeof data.detail === 'string' && data.detail) ||
            'Answers did not match our records.',
        );
      }
      setResetToken(data.reset_token);
      setStep(STEP.NEW_PASSWORD);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const savePassword = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/auth/reset-password-with-token`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reset_token: resetToken,
            new_password: newPassword,
            confirm_password: confirmPassword,
          }),
        },
      );
      const data = await readBody(r);
      if (!r.ok) {
        throw new Error(
          (typeof data.detail === 'string' && data.detail) ||
            'Could not set new password.',
        );
      }
      setStep(STEP.DONE);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const back = () => {
    if (step === STEP.ANSWERS) setStep(STEP.EMAIL);
    else if (step === STEP.NEW_PASSWORD) setStep(STEP.ANSWERS);
    else navigate('/login');
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4"
      data-testid="forgot-password-page"
    >
      <div className="w-full max-w-md bg-slate-800/60 backdrop-blur-xl border border-slate-700 rounded-2xl p-6 shadow-2xl">
        <button
          onClick={back}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-white mb-4"
          data-testid="forgot-back-btn"
        >
          <ArrowLeft className="w-3 h-3" />
          Back
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            {step === STEP.DONE ? (
              <CheckCircle2 className="w-6 h-6 text-white" />
            ) : (
              <KeyRound className="w-5 h-5 text-white" />
            )}
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">
              {step === STEP.DONE ? 'All set' : 'Forgot your password?'}
            </h1>
            <p className="text-xs text-gray-400">
              {step === STEP.EMAIL && 'Enter your email to start.'}
              {step === STEP.ANSWERS && 'Answer your security questions.'}
              {step === STEP.NEW_PASSWORD && 'Choose a new password.'}
              {step === STEP.DONE && 'You can now log in with your new password.'}
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-700/50 text-sm text-red-200">
            {error}
          </div>
        )}

        {step === STEP.EMAIL && (
          <form className="space-y-3" onSubmit={lookupQuestions}>
            <label className="block">
              <span className="text-xs text-gray-400">Email</span>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoFocus
                className="mt-1 bg-slate-900/60 border-slate-600 text-white"
                data-testid="forgot-email-input"
              />
            </label>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
              data-testid="forgot-lookup-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue'}
            </Button>
            <p className="text-[11px] text-gray-500 text-center pt-2">
              No security questions set up? Ask an admin to reset your password.
            </p>
          </form>
        )}

        {step === STEP.ANSWERS && (
          <form className="space-y-4" onSubmit={submitAnswers}>
            <div className="p-3 rounded-lg bg-slate-900/40 border border-slate-700 text-xs text-gray-300 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-indigo-300 shrink-0 mt-0.5" />
              Answers aren't case-sensitive. Typos and extra spaces are ignored.
            </div>
            {questions.map((q) => (
              <label key={q.question_id} className="block">
                <span className="text-sm text-white">{q.prompt}</span>
                <Input
                  type="text"
                  value={answers[q.question_id] || ''}
                  onChange={(e) =>
                    setAnswers((a) => ({
                      ...a,
                      [q.question_id]: e.target.value,
                    }))
                  }
                  className="mt-1 bg-slate-900/60 border-slate-600 text-white"
                  data-testid={`forgot-answer-${q.question_id}`}
                />
              </label>
            ))}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
              data-testid="forgot-verify-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify answers'}
            </Button>
          </form>
        )}

        {step === STEP.NEW_PASSWORD && (
          <form className="space-y-3" onSubmit={savePassword}>
            <label className="block">
              <span className="text-xs text-gray-400">New password</span>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoFocus
                className="mt-1 bg-slate-900/60 border-slate-600 text-white"
                data-testid="forgot-new-password"
              />
            </label>
            <label className="block">
              <span className="text-xs text-gray-400">Confirm password</span>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 bg-slate-900/60 border-slate-600 text-white"
                data-testid="forgot-confirm-password"
              />
            </label>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              data-testid="forgot-save-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save new password'}
            </Button>
          </form>
        )}

        {step === STEP.DONE && (
          <div className="space-y-4 text-center" data-testid="forgot-done">
            <p className="text-sm text-gray-200 leading-relaxed">
              Your password has been updated. You can log in now with your new
              password.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
              data-testid="forgot-goto-login"
            >
              Back to login
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
